/**
 * Safari-safe timezone resolver for the dynamic greeting / chat path.
 *
 * History: earlier revisions of this module also returned a `hour` derived
 * from `new Date().getHours()` and the server preferred that hour over the
 * IANA timezone. That made the greeting bucket depend on the user's *device*
 * clock — which in Safari is sometimes wrong, spoofed, or restricted by
 * ITP / fingerprint protection — and the greeting kept landing in the wrong
 * bucket (e.g. always "Good evening").
 *
 * New contract (Task #49): the browser returns ONLY the user's IANA
 * timezone string (or null). The server then computes the actual hour
 * from its own NTP-synced UTC clock projected into that zone. This matches
 * the pattern recommended for Safari (Intl-derived zone + server-authoritative
 * time) and removes any dependency on `Date.prototype.getHours()`.
 *
 * Returns `null` when the zone is missing/blank or `Intl.DateTimeFormat`
 * can't be used. Callers should then send `client_timezone: null` and the
 * server will omit the time-of-day clause (greeting path) or fall back to
 * IP-based geolocation (streaming chat path).
 */
export function getClientTimeInfo() {
    try {
        if (typeof Intl === "undefined" || !Intl.DateTimeFormat) {
            return null;
        }
        const resolved = Intl.DateTimeFormat().resolvedOptions();
        const candidate =
            resolved && typeof resolved.timeZone === "string"
                ? resolved.timeZone.trim()
                : "";
        if (!candidate) {
            return null;
        }
        // Sanity check: ask Intl to format a date in that zone. If it
        // throws, the string isn't a real IANA zone and we drop it.
        try {
            new Intl.DateTimeFormat("en-US", {
                hour: "2-digit",
                hourCycle: "h23",
                timeZone: candidate,
            }).format(new Date());
        } catch (_) {
            return null;
        }
        const locale =
            (typeof navigator !== "undefined" && navigator.language) ||
            "en-US";
        return {
            timeZone: candidate,
            locale,
        };
    } catch (_) {
        return null;
    }
}
